import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, ADMIN_TOKEN_KEY, authHeaders, API } from "@/lib/api";
import { formatJMD, totalStock } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Lock, Plus, Pencil, Trash2, Download, LogOut, Package, ShoppingCart, AlertTriangle, FileCheck2, UploadCloud, Smartphone, X, Bell, PackageX, RotateCcw } from "lucide-react";

const SIZES = ["S", "M", "L", "XL", "XXL"];
const FITS = ["Classic", "Boxy", "Mesh"];

const emptyProduct = () => ({
  title: "", description: "", flagship_image: "", gallery: [],
  price_cents: 0, fit: "Classic", color: "Black", collection: "Core",
  sizes: SIZES.map((s) => ({ size: s, stock: 0 })),
  limited_drop: false, drop_ends_at: null, best_seller: false, status: "active",
});

export default function Admin() {
  const [authed, setAuthed] = useState(!!localStorage.getItem(ADMIN_TOKEN_KEY));
  const [password, setPassword] = useState("");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [installEvt, setInstallEvt] = useState(null);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [activeTab, setActiveTab] = useState("products");
  const [importing, setImporting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [alertsOn, setAlertsOn] = useState(false);
  const [threshold, setThreshold] = useState(3);
  const [fees, setFees] = useState({ knutsford_islandwide: "", zipmail_postoffice: "" });
  const [promoCfg, setPromoCfg] = useState({ promo_active: true, sale_percent: 5, free_ship_min_items: 3, sale_ends_at: "" });
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setInstallEvt(e); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const installApp = async () => {
    if (!installEvt) {
      toast.info("Open your browser menu and tap 'Add to Home Screen' / 'Install app'.");
      return;
    }
    installEvt.prompt();
    await installEvt.userChoice;
    setInstallEvt(null);
  };

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await api.post("/admin/upload", fd, {
      headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
    });
    return `${API}/files/${r.data.path}`;
  };

  const urlB64ToUint8 = (b64) => {
    const pad = "=".repeat((4 - (b64.length % 4)) % 4);
    const base = (b64 + pad).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(base);
    return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
  };

  const enableAlerts = async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      toast.error("Push not supported on this browser. Install the app first.");
      return;
    }
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.error("Enable notifications in your browser settings."); return; }
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/vapid-public-key");
      if (!data.key) { toast.error("Push not configured on server."); return; }
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlB64ToUint8(data.key),
        });
      }
      await api.post("/admin/push/subscribe", { subscription: sub.toJSON() }, { headers: authHeaders() });
      await api.post("/admin/push/test", {}, { headers: authHeaders() });
      setAlertsOn(true);
      toast.success("Order alerts enabled — watch for the test notification.");
    } catch (e) {
      toast.error("Could not enable alerts. Try installing the app first.");
    }
  };

  const load = async () => {
    try {
      const [p, o, s] = await Promise.all([
        api.get("/products", { params: { include_drafts: true }, headers: authHeaders() }),
        api.get("/admin/orders", { headers: authHeaders() }),
        api.get("/admin/settings", { headers: authHeaders() }),
      ]);
      setProducts(p.data);
      setOrders(o.data);
      if (typeof s.data.low_stock_threshold === "number") setThreshold(s.data.low_stock_threshold);
      if (s.data.delivery_fees) {
        setFees({
          knutsford_islandwide: s.data.delivery_fees.knutsford_islandwide / 100,
          zipmail_postoffice: s.data.delivery_fees.zipmail_postoffice / 100,
        });
      }
      setPromoCfg({
        promo_active: s.data.promo_active ?? true,
        sale_percent: s.data.sale_percent ?? 5,
        free_ship_min_items: s.data.free_ship_min_items ?? 3,
        sale_ends_at: isoToLocal(s.data.sale_ends_at),
      });
    } catch {
      localStorage.removeItem(ADMIN_TOKEN_KEY);
      setAuthed(false);
    }
  };

  useEffect(() => {
    if (authed) load();
  }, [authed]);

  useEffect(() => {
    if (!authed) return;
    const tab = params.get("tab");
    if (tab && ["products", "orders", "stock"].includes(tab)) setActiveTab(tab);
    if (params.get("new") === "1") { openNew(); setActiveTab("products"); }
    if (tab || params.get("new")) setParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/admin/login", { password });
      localStorage.setItem(ADMIN_TOKEN_KEY, r.data.token);
      setAuthed(true);
      toast.success("Welcome back, boss.");
    } catch {
      toast.error("Incorrect password.");
    }
  };

  const logout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setAuthed(false);
  };

  const openNew = () => { setEditing(emptyProduct()); setDialogOpen(true); };
  const openEdit = (p) => {
    const sizes = SIZES.map((s) => p.sizes.find((x) => x.size === s) || { size: s, stock: 0 });
    setEditing({ ...p, sizes });
    setDialogOpen(true);
  };

  const saveProduct = async () => {
    if (!editing.title.trim()) { toast.error("Title required"); return; }
    const payload = { ...editing, price_cents: parseInt(editing.price_cents) || 0 };
    try {
      if (editing.id) {
        await api.put(`/admin/products/${editing.id}`, payload, { headers: authHeaders() });
      } else {
        await api.post("/admin/products", payload, { headers: authHeaders() });
      }
      toast.success("Product saved");
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`, { headers: authHeaders() });
    toast.success("Deleted");
    load();
  };

  const markOutOfStock = async (p) => {
    if (!window.confirm(`Mark "${p.title}" out of stock? This sets every size to 0.`)) return;
    try {
      await api.put(`/admin/products/${p.id}/out-of-stock`, null, { headers: authHeaders() });
      toast.success("Marked out of stock");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update stock");
    }
  };

  const restock = async (p) => {
    try {
      await api.put(`/admin/products/${p.id}/restock`, null, { headers: authHeaders() });
      toast.success("Back in stock");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No previous stock to restore");
    }
  };

  const saveThreshold = async (val) => {
    const n = parseInt(val);
    if (isNaN(n) || n < 0) { setThreshold(3); return; }
    setThreshold(n);
    try {
      await api.put("/admin/settings", null, { params: { low_stock_threshold: n }, headers: authHeaders() });
      toast.success(`Low-stock alerts at ${n} or fewer`);
    } catch (e) {
      toast.error("Could not save threshold");
    }
  };

  const isoToLocal = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const savePromo = async () => {
    try {
      const payload = {
        promo_active: !!promoCfg.promo_active,
        sale_percent: parseInt(promoCfg.sale_percent) || 0,
        free_ship_min_items: parseInt(promoCfg.free_ship_min_items) || 1,
        sale_ends_at: promoCfg.sale_ends_at ? new Date(promoCfg.sale_ends_at).toISOString() : null,
      };
      await api.put("/admin/promo", payload, { headers: authHeaders() });
      toast.success(payload.promo_active ? "Sale is live" : "Sale turned off");
    } catch (e) {
      toast.error("Could not save promo");
    }
  };

  const saveFees = async () => {
    try {
      const payload = {
        knutsford_islandwide: Math.round((parseFloat(fees.knutsford_islandwide) || 0) * 100),
        zipmail_postoffice: Math.round((parseFloat(fees.zipmail_postoffice) || 0) * 100),
      };
      await api.put("/admin/delivery-fees", payload, { headers: authHeaders() });
      toast.success("Delivery fees updated");
    } catch (e) {
      toast.error("Could not save delivery fees");
    }
  };

  const CARRIERS = { knutsford_islandwide: "Knutsford Express", zipmail_postoffice: "ZipMail" };

  const setOrderStatus = async (o, status) => {
    await api.put(`/admin/orders/${o.id}/status`, null, { params: { status }, headers: authHeaders() });
    toast.success(`Status: ${status}`);
    load();
  };

  const saveTracking = async (o, tracking_number) => {
    if (!tracking_number || !tracking_number.trim()) { toast.error("Enter a tracking number"); return; }
    try {
      await api.put(`/admin/orders/${o.id}/tracking`, null, { params: { tracking_number }, headers: authHeaders() });
      toast.success("Tracking saved · order marked Shipped");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save tracking");
    }
  };

  const viewProof = async (o) => {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    try {
      const res = await fetch(`${API}/admin/orders/${o.id}/payment-proof`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { toast.error("No receipt on file"); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch {
      toast.error("Could not load receipt");
    }
  };

  const exportCsv = async () => {    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    const res = await fetch(`${API}/admin/orders/export.csv`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "turahtid_orders.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <form onSubmit={login} data-testid="admin-login" className="w-full max-w-sm border border-white/10 bg-card p-8">
          <Lock className="w-8 h-8 text-jamaica-gold mx-auto mb-4" />
          <h1 className="font-display text-3xl tracking-tight text-center">ADMIN ACCESS</h1>
          <p className="text-xs text-zinc-500 text-center mb-6">Turahtid control room</p>
          <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Password</Label>
          <Input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="bg-background border-border rounded-none h-11 mb-4 focus-visible:ring-jamaica-gold" />
          <Button data-testid="admin-login-btn" type="submit" className="w-full py-6 bg-jamaica-gold hover:bg-yellow-300 text-black font-bold uppercase tracking-widest rounded-none">Enter</Button>
        </form>
      </div>
    );
  }

  const lowStock = products.filter((p) => totalStock(p) <= 5);
  const pendingOrders = orders.filter((o) => o.status === "pending").length;

  const setSize = (i, stock) => {
    const sizes = [...editing.sizes];
    sizes[i] = { ...sizes[i], stock: parseInt(stock) || 0 };
    setEditing({ ...editing, sizes });
  };
  const setGallery = (val) => setEditing({ ...editing, gallery: val.split("\n").map((s) => s.trim()).filter(Boolean) });

  const onFlagshipFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImg(true);
    try {
      const url = await uploadImage(file);
      setEditing((ed) => ({ ...ed, flagship_image: url }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
    setUploadingImg(false);
    e.target.value = "";
  };

  const onGalleryFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingImg(true);
    try {
      const urls = [];
      for (const f of files) urls.push(await uploadImage(f));
      setEditing((ed) => ({ ...ed, gallery: [...(ed.gallery || []), ...urls].slice(0, 4) }));
      toast.success(`${urls.length} photo(s) added`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
    setUploadingImg(false);
    e.target.value = "";
  };

  const removeGalleryImage = (idx) => setEditing((ed) => ({ ...ed, gallery: ed.gallery.filter((_, i) => i !== idx) }));

  const filenameToTitle = (name) => {
    const base = (name || "").replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ").trim();
    return base ? base.replace(/\b\w/g, (c) => c.toUpperCase()) : "Untitled Draft";
  };

  const importPhotos = async (fileList) => {
    const imgs = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (!imgs.length) { toast.error("Drop image files only"); return; }
    setImporting(true);
    let created = 0;
    for (const f of imgs) {
      try {
        const url = await uploadImage(f);
        await api.post("/admin/products", {
          title: filenameToTitle(f.name),
          description: "",
          flagship_image: url,
          gallery: [],
          price_cents: 0,
          fit: "Classic",
          color: "Black",
          collection: "Core",
          sizes: SIZES.map((s) => ({ size: s, stock: 0 })),
          limited_drop: false,
          best_seller: false,
          featured: false,
          status: "draft",
        }, { headers: authHeaders() });
        created++;
      } catch (e) {
        /* keep going through the batch */
      }
    }
    setImporting(false);
    if (created) toast.success(`${created} draft product(s) created — edit price & stock, then publish`);
    else toast.error("Import failed. Try again.");
    load();
  };

  const onDrop = (e) => { e.preventDefault(); setDragOver(false); importPhotos(e.dataTransfer.files); };

  return (
    <div data-testid="admin-page" className="pt-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
      <div className="flex items-center justify-between py-6">
        <div>
          <h1 className="font-display text-4xl tracking-tight">CONTROL ROOM</h1>
          <p className="font-mono text-xs text-zinc-500 uppercase tracking-widest mt-1">
            {products.length} products · {orders.length} orders · {pendingOrders} pending
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="admin-enable-alerts" onClick={enableAlerts} variant="outline" className={`rounded-none uppercase text-xs tracking-widest ${alertsOn ? "border-jamaica-green/60 text-jamaica-green" : "border-border"}`}>
            <Bell className="w-4 h-4 mr-2" /> {alertsOn ? "Alerts On" : "Order Alerts"}
          </Button>
          <Button data-testid="admin-install-app" onClick={installApp} className="rounded-none bg-jamaica-gold hover:bg-yellow-300 text-black uppercase text-xs tracking-widest font-bold">
            <Smartphone className="w-4 h-4 mr-2" /> Install App
          </Button>
          <Button data-testid="admin-logout" onClick={logout} variant="outline" className="rounded-none border-border uppercase text-xs tracking-widest">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-card border border-border rounded-none">
          <TabsTrigger value="products" data-testid="tab-products" className="rounded-none data-[state=active]:bg-jamaica-gold data-[state=active]:text-black uppercase text-xs tracking-widest"><Package className="w-4 h-4 mr-2" />Products</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders" className="rounded-none data-[state=active]:bg-jamaica-gold data-[state=active]:text-black uppercase text-xs tracking-widest"><ShoppingCart className="w-4 h-4 mr-2" />Orders</TabsTrigger>
          <TabsTrigger value="stock" data-testid="tab-stock" className="rounded-none data-[state=active]:bg-jamaica-gold data-[state=active]:text-black uppercase text-xs tracking-widest"><AlertTriangle className="w-4 h-4 mr-2" />Stock</TabsTrigger>
        </TabsList>

        {/* PRODUCTS */}
        <TabsContent value="products" className="mt-6">
          <div
            data-testid="bulk-import-dropzone"
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`mb-4 border-2 border-dashed p-6 text-center transition-colors ${dragOver ? "border-jamaica-gold bg-jamaica-gold/5" : "border-white/15"}`}
          >
            <UploadCloud className="w-6 h-6 mx-auto text-jamaica-gold mb-2" />
            <p className="font-heading font-bold uppercase text-sm">Bulk Photo Import</p>
            <p className="text-xs text-zinc-500 mt-1">Drag &amp; drop product photos here to auto-create drafts — one draft per photo.</p>
            <label data-testid="bulk-import-input" className="cursor-pointer inline-flex items-center gap-2 border border-jamaica-gold/50 text-jamaica-gold hover:bg-jamaica-gold hover:text-black px-3 py-2 font-mono text-[10px] uppercase tracking-widest mt-3">
              <UploadCloud className="w-3.5 h-3.5" /> {importing ? "Importing…" : "Choose Photos"}
              <input type="file" accept="image/*" multiple onChange={(e) => { importPhotos(e.target.files); e.target.value = ""; }} className="hidden" disabled={importing} />
            </label>
          </div>
          <Button data-testid="add-product-btn" onClick={openNew} className="mb-4 bg-jamaica-green hover:bg-green-700 text-white rounded-none uppercase text-xs tracking-widest font-bold">
            <Plus className="w-4 h-4 mr-2" /> Add Product
          </Button>
          <div className="border border-white/10 divide-y divide-white/5">
            {products.map((p) => (
              <div key={p.id} data-testid={`admin-product-${p.id}`} className="flex items-center gap-4 p-3">
                <img src={p.flagship_image} alt={p.title} className="w-12 h-14 object-cover bg-zinc-900" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-heading font-bold uppercase text-sm truncate">{p.title}</h3>
                    {p.status === "draft" && <span className="font-mono text-[9px] uppercase bg-zinc-700 px-1.5 py-0.5">Draft</span>}
                    {p.limited_drop && <span className="font-mono text-[9px] uppercase bg-red-600 px-1.5 py-0.5">Drop</span>}
                    {p.best_seller && <span className="font-mono text-[9px] uppercase bg-jamaica-gold text-black px-1.5 py-0.5">Best</span>}
                    {totalStock(p) <= 0 && <span data-testid={`out-of-stock-badge-${p.id}`} className="font-mono text-[9px] uppercase bg-zinc-800 border border-red-500/50 text-red-400 px-1.5 py-0.5">Out of Stock</span>}
                  </div>
                  <p className="font-mono text-xs text-zinc-500">{p.fit} · {formatJMD(p.price_cents)} · {totalStock(p)} in stock</p>
                </div>
                {totalStock(p) > 0 && (
                  <button data-testid={`out-of-stock-${p.id}`} onClick={() => markOutOfStock(p)} title="Mark out of stock" className="p-2 text-zinc-400 hover:text-red-400"><PackageX className="w-4 h-4" /></button>
                )}
                {totalStock(p) <= 0 && p.prev_sizes && p.prev_sizes.length > 0 && (
                  <button data-testid={`restock-${p.id}`} onClick={() => restock(p)} title="Back in stock" className="p-2 text-zinc-400 hover:text-jamaica-green"><RotateCcw className="w-4 h-4" /></button>
                )}
                <button data-testid={`edit-product-${p.id}`} onClick={() => openEdit(p)} className="p-2 hover:text-jamaica-gold"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`delete-product-${p.id}`} onClick={() => deleteProduct(p.id)} className="p-2 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* ORDERS */}
        <TabsContent value="orders" className="mt-6">
          <Button data-testid="export-csv-btn" onClick={exportCsv} className="mb-4 bg-jamaica-gold hover:bg-yellow-300 text-black rounded-none uppercase text-xs tracking-widest font-bold">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          {orders.length === 0 && <p className="text-zinc-500 font-mono text-sm py-8 text-center">No orders yet.</p>}
          <div className="space-y-3">
            {orders.map((o) => {
              const os = o.order_status || "Pending Payment";
              const canTrack = os === "Payment Confirmed" || os === "Shipped" || os === "Delivered";
              return (
              <div key={o.id} data-testid={`admin-order-${o.id}`} className="border border-white/10 bg-card p-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <span className="font-display text-xl text-jamaica-gold tracking-wider">{o.order_reference || o.order_number}</span>
                    <span className="font-mono text-xs text-zinc-500 ml-3">{o.customer.full_name} · {o.customer.parish}</span>
                  </div>
                  <span data-testid={`order-status-${o.id}`} className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-jamaica-gold/40 text-jamaica-gold">{os}</span>
                </div>
                <div className="mt-2 text-sm text-zinc-400">
                  {o.items.map((i, idx) => <span key={idx}>{i.title} ({i.size}×{i.qty}){idx < o.items.length - 1 ? ", " : ""}</span>)}
                </div>
                <div className="flex items-center justify-between mt-2 font-mono text-xs text-zinc-500">
                  <span>{o.customer.phone} · {o.payment_method}</span>
                  <span className="text-jamaica-gold">{formatJMD(o.total_cents)}</span>
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  {o.payment_proof_path ? (
                    <button data-testid={`view-proof-${o.id}`} onClick={() => viewProof(o)} className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border text-jamaica-green border-jamaica-green/50 hover:bg-jamaica-green hover:text-black flex items-center gap-1.5"><FileCheck2 className="w-3.5 h-3.5" /> View Receipt</button>
                  ) : (
                    <span data-testid={`no-proof-${o.id}`} className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">No receipt yet</span>
                  )}
                  {os === "Pending Payment" && (
                    <button data-testid={`confirm-payment-${o.id}`} onClick={() => setOrderStatus(o, "Payment Confirmed")} className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border text-jamaica-gold border-jamaica-gold/50 hover:bg-jamaica-gold hover:text-black">Mark Payment Confirmed</button>
                  )}
                  {os === "Shipped" && (
                    <button data-testid={`deliver-${o.id}`} onClick={() => setOrderStatus(o, "Delivered")} className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border text-jamaica-green border-jamaica-green/50 hover:bg-jamaica-green hover:text-black">Mark Delivered</button>
                  )}
                </div>
                <div className="mt-3 border-t border-white/5 pt-3">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                    Tracking Number · <span className="text-jamaica-gold">{CARRIERS[o.tracking_carrier] || "—"}</span>
                  </p>
                  <div className="flex gap-2">
                    <Input
                      data-testid={`tracking-input-${o.id}`}
                      defaultValue={o.tracking_number || ""}
                      disabled={!canTrack}
                      id={`trk-${o.id}`}
                      className="bg-background border-border rounded-none h-9 text-sm disabled:opacity-40"
                      placeholder={canTrack ? "Enter tracking number" : "Confirm payment first"}
                    />
                    <button
                      data-testid={`save-tracking-${o.id}`}
                      disabled={!canTrack}
                      onClick={() => saveTracking(o, document.getElementById(`trk-${o.id}`).value)}
                      className="font-mono text-[10px] uppercase tracking-widest px-3 border border-jamaica-gold/50 text-jamaica-gold hover:bg-jamaica-gold hover:text-black disabled:opacity-40"
                    >Save</button>
                  </div>
                </div>
              </div>
            );})}
          </div>
        </TabsContent>

        {/* STOCK */}
        <TabsContent value="stock" className="mt-6">
          <div data-testid="promo-settings" className="mb-4 border border-jamaica-gold/30 bg-card p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="font-heading font-bold uppercase text-sm">Store Sale</p>
                <p className="text-[11px] text-zinc-500">Turn the sitewide sale on/off and set the terms.</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">{promoCfg.promo_active ? "On" : "Off"}</span>
                <Switch data-testid="promo-active-switch" checked={!!promoCfg.promo_active} onCheckedChange={(v) => setPromoCfg({ ...promoCfg, promo_active: v })} />
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Discount %</label>
                <Input data-testid="promo-percent" type="number" min={0} max={90} value={promoCfg.sale_percent} onChange={(e) => setPromoCfg({ ...promoCfg, sale_percent: e.target.value === "" ? "" : parseInt(e.target.value) })} className="bg-background border-border rounded-none h-9 mt-1" />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Free ship at (items)</label>
                <Input data-testid="promo-freeship" type="number" min={1} value={promoCfg.free_ship_min_items} onChange={(e) => setPromoCfg({ ...promoCfg, free_ship_min_items: e.target.value === "" ? "" : parseInt(e.target.value) })} className="bg-background border-border rounded-none h-9 mt-1" />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Ends (optional)</label>
                <Input data-testid="promo-ends" type="datetime-local" value={promoCfg.sale_ends_at} onChange={(e) => setPromoCfg({ ...promoCfg, sale_ends_at: e.target.value })} className="bg-background border-border rounded-none h-9 mt-1" />
              </div>
            </div>
            <div className="flex items-center gap-3 mt-3">
              <Button data-testid="save-promo-btn" onClick={savePromo} className="bg-jamaica-green hover:bg-green-700 text-white rounded-none uppercase text-xs tracking-widest font-bold">Save Sale</Button>
              {promoCfg.sale_ends_at && <button data-testid="clear-promo-end" onClick={() => setPromoCfg({ ...promoCfg, sale_ends_at: "" })} className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-jamaica-gold">Clear end date</button>}
            </div>
          </div>
          <div data-testid="delivery-fee-settings" className="mb-4 border border-jamaica-green/30 bg-card p-4">
            <p className="font-heading font-bold uppercase text-sm">Delivery Fees</p>
            <p className="text-[11px] text-zinc-500 mb-3">Amounts in J$. Changes apply to checkout immediately.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Knutsford Express (J$)</label>
                <Input data-testid="fee-knutsford" type="number" min={0} value={fees.knutsford_islandwide} onChange={(e) => setFees({ ...fees, knutsford_islandwide: e.target.value })} className="bg-background border-border rounded-none h-9 mt-1" />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">ZipMail (J$)</label>
                <Input data-testid="fee-zipmail" type="number" min={0} value={fees.zipmail_postoffice} onChange={(e) => setFees({ ...fees, zipmail_postoffice: e.target.value })} className="bg-background border-border rounded-none h-9 mt-1" />
              </div>
            </div>
            <Button data-testid="save-fees-btn" onClick={saveFees} className="mt-3 bg-jamaica-green hover:bg-green-700 text-white rounded-none uppercase text-xs tracking-widest font-bold">Save Fees</Button>
          </div>
          <div className="flex items-center flex-wrap gap-3 mb-4 border border-white/10 bg-card p-3">
            <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Low-stock alert threshold</label>
            <Input
              data-testid="low-stock-threshold"
              type="number"
              min={0}
              value={threshold}
              onChange={(e) => setThreshold(e.target.value === "" ? "" : parseInt(e.target.value))}
              onBlur={(e) => saveThreshold(e.target.value)}
              className="bg-background border-border rounded-none h-9 w-20"
            />
            <span className="font-mono text-[10px] text-zinc-500">Push an alert when an order drops a product's total stock to this or below.</span>
          </div>
          {lowStock.length > 0 && (
            <p className="font-mono text-xs text-red-400 uppercase tracking-widest mb-3">{lowStock.length} product(s) low or out of stock</p>
          )}
          <div className="border border-white/10 divide-y divide-white/5">
            {products.map((p) => (
              <div key={p.id} className="p-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading font-bold uppercase text-sm">{p.title}</h3>
                  <span className={`font-mono text-xs ${totalStock(p) <= 5 ? "text-red-400" : "text-zinc-400"}`}>{totalStock(p)} total</span>
                </div>
                <div className="flex gap-2 mt-2">
                  {p.sizes.map((s) => (
                    <span key={s.size} className={`font-mono text-[10px] uppercase px-2 py-1 border ${s.stock <= 0 ? "border-zinc-700 text-zinc-600 line-through" : s.stock <= 3 ? "border-red-500/40 text-red-400" : "border-border text-zinc-300"}`}>
                      {s.size}:{s.stock}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* EDIT DIALOG */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-popover border-border rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl tracking-wide">{editing?.id ? "EDIT PRODUCT" : "NEW PRODUCT"}</DialogTitle><DialogDescription className="sr-only">Product details form</DialogDescription></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Title</Label>
                <Input data-testid="pf-title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="bg-background border-border rounded-none" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Description</Label>
                <Textarea data-testid="pf-desc" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="bg-background border-border rounded-none" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Flagship Photo</Label>
                <div className="flex items-center gap-3 mt-1">
                  {editing.flagship_image ? (
                    <img src={editing.flagship_image} alt="flagship" className="w-16 h-20 object-cover bg-zinc-900 border border-white/10" />
                  ) : (
                    <div className="w-16 h-20 bg-zinc-900 border border-dashed border-white/15 flex items-center justify-center text-zinc-600"><UploadCloud className="w-5 h-5" /></div>
                  )}
                  <label data-testid="pf-image-upload" className="cursor-pointer inline-flex items-center gap-2 border border-jamaica-gold/50 text-jamaica-gold hover:bg-jamaica-gold hover:text-black px-3 py-2 font-mono text-[10px] uppercase tracking-widest">
                    <UploadCloud className="w-3.5 h-3.5" /> {uploadingImg ? "Uploading…" : "Upload Photo"}
                    <input type="file" accept="image/*" onChange={onFlagshipFile} className="hidden" disabled={uploadingImg} />
                  </label>
                </div>
                <Input data-testid="pf-image" value={editing.flagship_image} onChange={(e) => setEditing({ ...editing, flagship_image: e.target.value })} className="bg-background border-border rounded-none mt-2 text-xs" placeholder="…or paste an image URL" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Gallery Photos (up to 4)</Label>
                {(editing.gallery || []).length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-1">
                    {editing.gallery.map((g, i) => (
                      <div key={i} className="relative">
                        <img src={g} alt={`gallery ${i + 1}`} className="w-16 h-20 object-cover bg-zinc-900 border border-white/10" />
                        <button type="button" data-testid={`pf-gallery-remove-${i}`} onClick={() => removeGalleryImage(i)} className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full w-4 h-4 flex items-center justify-center"><X className="w-2.5 h-2.5" /></button>
                      </div>
                    ))}
                  </div>
                )}
                {(editing.gallery || []).length < 4 && (
                  <label data-testid="pf-gallery-upload" className="cursor-pointer inline-flex items-center gap-2 border border-jamaica-gold/50 text-jamaica-gold hover:bg-jamaica-gold hover:text-black px-3 py-2 font-mono text-[10px] uppercase tracking-widest mt-2">
                    <UploadCloud className="w-3.5 h-3.5" /> {uploadingImg ? "Uploading…" : "Add Gallery Photos"}
                    <input type="file" accept="image/*" multiple onChange={onGalleryFiles} className="hidden" disabled={uploadingImg} />
                  </label>
                )}
                <Textarea data-testid="pf-gallery" value={(editing.gallery || []).join("\n")} onChange={(e) => setGallery(e.target.value)} className="bg-background border-border rounded-none mt-2 text-xs" placeholder="…or paste image URLs, one per line" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Price (cents JMD)</Label>
                  <Input data-testid="pf-price" type="number" value={editing.price_cents} onChange={(e) => setEditing({ ...editing, price_cents: e.target.value })} className="bg-background border-border rounded-none" />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Color</Label>
                  <Input data-testid="pf-color" value={editing.color} onChange={(e) => setEditing({ ...editing, color: e.target.value })} className="bg-background border-border rounded-none" />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Fit</Label>
                  <Select value={editing.fit} onValueChange={(v) => setEditing({ ...editing, fit: v })}>
                    <SelectTrigger data-testid="pf-fit" className="bg-background border-border rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-popover border-border">{FITS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Collection</Label>
                  <Input data-testid="pf-collection" value={editing.collection} onChange={(e) => setEditing({ ...editing, collection: e.target.value })} className="bg-background border-border rounded-none" />
                </div>
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Stock per size</Label>
                <div className="grid grid-cols-5 gap-2 mt-1">
                  {editing.sizes.map((s, i) => (
                    <div key={s.size}>
                      <span className="font-mono text-[10px] text-zinc-500">{s.size}</span>
                      <Input data-testid={`pf-stock-${s.size}`} type="number" value={s.stock} onChange={(e) => setSize(i, e.target.value)} className="bg-background border-border rounded-none h-9 px-2 text-sm" />
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center gap-2"><Switch data-testid="pf-limited" checked={editing.limited_drop} onCheckedChange={(v) => setEditing({ ...editing, limited_drop: v })} /><span className="font-mono text-xs uppercase">Limited Drop</span></label>
                <label className="flex items-center gap-2"><Switch data-testid="pf-best" checked={editing.best_seller} onCheckedChange={(v) => setEditing({ ...editing, best_seller: v })} /><span className="font-mono text-xs uppercase">Best Seller</span></label>
                <label className="flex items-center gap-2"><Switch data-testid="pf-active" checked={editing.status === "active"} onCheckedChange={(v) => setEditing({ ...editing, status: v ? "active" : "draft" })} /><span className="font-mono text-xs uppercase">Active</span></label>
              </div>
              {editing.limited_drop && (
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Drop Ends (ISO date, optional)</Label>
                  <Input data-testid="pf-dropdate" type="datetime-local" value={editing.drop_ends_at ? editing.drop_ends_at.slice(0, 16) : ""} onChange={(e) => setEditing({ ...editing, drop_ends_at: e.target.value ? new Date(e.target.value).toISOString() : null })} className="bg-background border-border rounded-none" />
                </div>
              )}
              <Button data-testid="save-product-btn" onClick={saveProduct} className="w-full py-6 bg-jamaica-gold hover:bg-yellow-300 text-black font-bold uppercase tracking-widest rounded-none">Save Product</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
